import { createContext, forwardRef, useContext, useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { useTileDroppable } from '.';
import { setAlpha } from '../../design-tokens/utils';
import { useReactDndConstructs } from '../../dnd/ReactDndContext';
import { EarconName, useEarcon } from '../../hooks/useEarcon';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { getDndDesignTokens } from '../../style/reactDnd/DndStyleTokens';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import { Checkbox } from '../Checkbox';
import { Input } from '../Input';
import { InvisibleText } from '../InvisibleText';
import { Flow } from '../Layouts/Flow';
import { Form } from '../Layouts/Form';
import { ObjectMarker } from '../ObjectMarker';
import { ToolbarSpacer } from '../Toolbar/__internal__';
import { withLabels } from '../withLabels';
import { TileContainer, TileFooter, TileHeader, useInteractiveTile, useTileKeyboardTooltip } from './__internal__';
import { TileDragType } from './TileDragPreview';
import bundle from './NovaComponents-Tile-gui-icu.properties';
import cn from './Tile.css.json';
import './Tile.css';
import { useDirectSpeech } from '../DirectSpeech';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useHtmlId = createUseHtmlId('Tile');
const useTileId = createUseHtmlId('tile-container');
const useFavoriteId = createUseHtmlId('favorite-object-marker');
const useTextLocalizer = createUseTextLocalizer(bundle);
const InputWithLabels = withLabels(Input);
const TilesContext = /*#__PURE__*/createContext(null);
export const useTilesContext = () => useContext(TilesContext);
export const TilesContextProvider = props => {
  return /*#__PURE__*/_jsx(TilesContext.Provider, {
    value: props.value,
    children: props.children
  });
};
const NonDraggableTile = /*#__PURE__*/forwardRef(function Tile(props, ref) {
  const tilesContext = useTilesContext();
  const titleId = useHtmlId();
  const dt = useDesignTokens();
  const defaultHeaderIconId = useTileId();
  const selectionTextId = useTileId();
  const interactiveModeAnnouncementId = useTileId();
  const thumbnailId = useTileId();
  const objectMarkerIds = useTileId(props.objectMarkers?.length ?? 0);
  const favoriteObjectMarkerId = useFavoriteId();
  const headerIconId = (props.avatarProps ? props.avatarProps.id : props.icon?.id) ?? defaultHeaderIconId;
  const localize = useTextLocalizer();
  const directSpeech = useDirectSpeech();
  const tileRef = useRef(null);
  const bodyRef = useRef(null);
  const customFooterRef = useRef(null);
  const customContentRefs = useMemo(() => [bodyRef, customFooterRef], []);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const hideTileCheckbox = props.hideCheckbox ?? tilesContext?.hideCheckbox;
  const onTileSelect = props.onSelect ?? tilesContext?.onSelect;
  const tileSelected = props.selected ?? tilesContext?.isSelected?.(props.index) ?? false;
  const selectionMode = props.selectionMode ?? tilesContext?.selectionMode ?? 'single';
  const orientation = props.orientation ?? 'vertical';
  const isGridCell = props.isGridCell ?? tilesContext?.isGridCell;
  const role = props.role ?? tilesContext?.role;

  // F2 support enabled by default
  const disableInteractiveMode = selectionMode === 'multi' ? false : props.disableInteractiveMode ?? tilesContext?.disableInteractiveMode ?? false;
  const setKeyboardTooltipRef = useTileKeyboardTooltip({
    ...props,
    disableInteractiveMode
  });

  // when interactive mode is enabled, Tiles can have two modes: browse and interactive.
  // User can toggle tile between browse and interactive using F2.  If not interactive, the tile itself
  // can still receive keyboard focus. Default is to start in browse mode.
  const {
    isInteractive,
    ...interactiveHandlers
  } = useInteractiveTile({
    tileRef,
    customContentRefs,
    disableInteractiveMode,
    suppressCustomContentFocus: props.tabIndex !== -1,
    isMenuOpen
  });
  const containerTabIndex = disableInteractiveMode && props.tabIndex !== undefined ? props.tabIndex : disableInteractiveMode || !isInteractive ? 0 : -1;
  const generateObjectMetadataTableRow = (key, value, isAddtional = false) => {
    return value ? /*#__PURE__*/_jsx(InputWithLabels, {
      label: isAddtional ? localize('objectMetadata.addition.label', {
        key
      }) : localize(`objectMetadata.${key}.label`),
      "aria-labelledby": key,
      type: "text",
      editable: false,
      value: value,
      onChange: () => {},
      fieldWidth: "100%",
      tabindex: containerTabIndex === -1 ? 0 : -1,
      testId: postfixTestId(props.testId, `metadata-${key}`),
      wrapLabel: false,
      labelStyle: {
        fontSize: dt.tokens['font-size-body-text-s']
      },
      inputStyle: {
        fontSize: dt.tokens['font-size-body-text-s']
      },
      className: cn.metadataTableRow
    }, key) : null;
  };
  const focusHandler = () => {
    // if the selection mode is single, don't announce "not selected"
    // for all selection modes with grid cells, "selected" is announced by aria-selected
    const suppressSelectedAnnouncement = selectionMode === 'none' || selectionMode === 'single' || tileSelected || !isGridCell;
    if (!suppressSelectedAnnouncement) {
      directSpeech?.notify(localize('unselected.aria'));
    }
  };
  const generateObjectMetadataTable = () => {
    const metadataTable = [];
    const {
      creator,
      modifier,
      creationDate,
      modificationDate,
      location,
      ...addition
    } = props.metadata ?? {};
    if (creator) {
      metadataTable.push(generateObjectMetadataTableRow('creator', creator));
    }
    if (modifier) {
      metadataTable.push(generateObjectMetadataTableRow('modifier', modifier));
    }
    if (creationDate) {
      metadataTable.push(generateObjectMetadataTableRow('creationDate', creationDate));
    }
    if (modificationDate) {
      metadataTable.push(generateObjectMetadataTableRow('modificationDate', modificationDate));
    }
    if (location) {
      metadataTable.push(generateObjectMetadataTableRow('location', location));
    }
    for (const name in addition) {
      const value = addition[name];
      if (value) {
        metadataTable.push(generateObjectMetadataTableRow(name, value, true));
      }
    }
    return metadataTable.length > 0 ? /*#__PURE__*/_jsx(Form, {
      labelPosition: "left",
      fieldSpacing: "xs",
      fieldLabelWidth: "20rem",
      className: cn.metadataTableForm,
      children: metadataTable
    }) : null;
  };
  const metadataTable = generateObjectMetadataTable();
  const menuButtonProps = props.menuButtonProps ? {
    ...props.menuButtonProps,
    onOpen: () => {
      props.menuButtonProps?.onOpen?.();
      setIsMenuOpen(true);
    },
    onDismiss: restoreFocus => {
      props.menuButtonProps?.onDismiss?.(restoreFocus);
      setIsMenuOpen(false);
    }
  } : undefined;
  return /*#__PURE__*/_jsxs(TileContainer, {
    testId: props.testId,
    ref: element => {
      setRef(ref, element);
      setRef(tileRef, element);
      setKeyboardTooltipRef(element);
    },
    style: {
      ...renderInlineCssVars({
        pd: dt.tokens['space-base-m']
      }, 'tile-content'),
      ...renderInlineCssVars({
        pd: dt.tokens['space-base-s']
      }, 'tile-thumbnail-container'),
      ...renderInlineCssVars({
        'icon-fg': dt.tokens['color-body-icon'],
        bg: dt.themeVariant === 'high-contrast' ? dt.tokens['color-container-background-0'] : setAlpha(dt.tokens['color-container-background-0'], 0.4),
        'margin-xxs': dt.tokens['space-base-xxs']
      }, 'tile-object-marker'),
      ...props.style
    },
    className: cx(cn.tile, props.onSelect && cn.selectable, tileSelected && cn.selected, props.className),
    isInteractive: isInteractive,
    ariaLabel: props.ariaLabel,
    ...(disableInteractiveMode ? {} : interactiveHandlers),
    onFocus: focusHandler,
    index: props.index,
    tabIndex: containerTabIndex,
    selected: tileSelected,
    onSelect: onTileSelect,
    onOpen: props.onOpen,
    ariaLabelledBy: props.ariaLabelledBy ?? (props.ariaLabel ? undefined : [...(props.title ? [titleId] : []),
    // add certain content when interactive mode is disabled because then nothing is focusable
    ...(props.thumbnail && (props.thumbnailAriaNotHidden || props.disableInteractiveMode) ? [thumbnailId] : []), ...(props.avatarProps && props.avatarProps.ariaLabel !== '' || !props.avatarProps && props.icon && !props.objectIconAriaHidden ? [headerIconId] : []),
    // subTitle is only shown when there is a title
    ...(props.title && props.subTitle && props.disableInteractiveMode ? [`${titleId}-subtitle`] : []), ...(props.description && props.disableInteractiveMode ? [`${titleId}-description`] : [])].join(' ')),
    ariaDescribedBy: props.ariaDescribedBy ?? [...(!tilesContext?.suppressInteractiveModeAnnouncement && disableInteractiveMode !== true ? [interactiveModeAnnouncementId] : []), ...(props.favorited ? [favoriteObjectMarkerId] : []), ...objectMarkerIds, ...(isGridCell || selectionMode === 'single' && !tileSelected || selectionMode === 'none' ? [] : [selectionTextId])].join(' '),
    ariaSelected:
    //  aria-selected is only valid when role="option" or "gridcell"
    (isGridCell || disableInteractiveMode) && selectionMode !== 'none' ? tileSelected : undefined,
    disableInteractiveMode: disableInteractiveMode,
    disableClickDelay: props.disableClickDelay,
    onContextMenu: props.onContextMenu,
    variant: props.variant,
    isGridCell: isGridCell,
    role: role,
    focusLockProps: {
      ...props.focusLockProps,
      onActivation: node => {
        props.focusLockProps?.onActivation?.(node);
        if (!disableInteractiveMode) {
          // Explicitly fire the onFocus handler when FocusLock is activated because it does not
          // trigger the browser onfocus event. See details in NOVACOMMONS-20600.
          interactiveHandlers.onFocus();
        }
      }
    },
    children: [/*#__PURE__*/_jsx(InvisibleText, {
      id: interactiveModeAnnouncementId,
      children: localize('interactiveMode.hint.keyboardTooltip.label')
    }), !isGridCell && !(selectionMode === 'single' && !tileSelected) && selectionMode !== 'none' && /*#__PURE__*/_jsx(InvisibleText, {
      id: selectionTextId,
      children: localize(tileSelected ? 'selected.aria' : 'unselected.aria')
    }), /*#__PURE__*/_jsxs("div", {
      className: cn.bar,
      children: [!hideTileCheckbox ? /*#__PURE__*/_jsx(Checkbox, {
        focusable: isInteractive,
        ariaLabelledBy: props.checkBoxAriaLabelledBy ?? (
        // By default, the checkbox is aria-labelledby the title element if no aria-label is provided
        !props.checkBoxAriaLabel && props.title ? titleId : undefined),
        ariaLabel: props.checkBoxAriaLabel,
        className: cn.checkbox
        // This overrules the hiding of the checkbox except when checked.
        ,
        style: !isInteractive ? undefined : {
          display: 'inline-block'
        },
        checked: !!tileSelected,
        testId: postfixTestId(props.testId, 'checkbox'),
        onPress: event => {
          onTileSelect?.({
            index: props.index ?? undefined,
            selectionArea: 'checkbox',
            altKey: event.altKey,
            ctlKey: event.ctrlKey,
            metaKey: event.metaKey,
            shiftKey: event.shiftKey
          });
        },
        theme: () => ({
          '--checkbox-spacing-xxs': '0'
        })
      }) : undefined, /*#__PURE__*/_jsx(ToolbarSpacer, {}), props.favorited ? /*#__PURE__*/_jsx(ObjectMarker, {
        className: cn.objectMarker,
        type: "Favorite",
        hideLabel: true,
        focusable: isInteractive,
        testId: postfixTestId(props.testId, 'favorite-icon'),
        id: favoriteObjectMarkerId
      }) : null]
    }), /*#__PURE__*/_jsxs("div", {
      className: cx(cn.contentContainer, orientation === 'horizontal' ? cn.horizontal : cn.vertical),
      children: [props.thumbnail ? /*#__PURE__*/_jsx("div", {
        id: thumbnailId,
        tabIndex: isInteractive && props.thumbnailAriaNotHidden ? 0 : undefined,
        "data-testid": postfixTestId(props.testId, 'thumbnail'),
        className: cn.thumbnailContainer,
        style: props.thumbnailContainerStyle,
        "aria-hidden": !props.thumbnailAriaNotHidden,
        children: props.thumbnail
      }) : null, hasContent(props) ? /*#__PURE__*/_jsxs(Flow, {
        orientation: "vertical",
        className: cn.content,
        style: props.tileContentStyle,
        spacing: "s",
        testId: postfixTestId(props.testId, 'content-flow'),
        children: [/*#__PURE__*/_jsx(TileHeader, {
          testId: postfixTestId(props.testId, 'header'),
          tabIndex: disableInteractiveMode ? -1 : containerTabIndex === 0 ? -1 : 0,
          titleId: titleId,
          onOpen: props.onOpen,
          icon: props.icon ? {
            ...props.icon,
            id: headerIconId
          } : undefined,
          avatarProps: props.avatarProps ? {
            ...props.avatarProps,
            id: headerIconId,
            focusable: props.avatarProps.focusable ?? props.avatarProps.ariaLabel !== ''
          } : undefined,
          title: props.title,
          subTitle: props.subTitle,
          suppressTitleAsLink: props.suppressTitleAsLink,
          objectIconAriaHidden: props.objectIconAriaHidden,
          menuButtonProps: menuButtonProps,
          description: props.description
        }), props.showObjectMetadataTable || props.body ? /*#__PURE__*/_jsxs("div", {
          ref: bodyRef,
          "data-testid": postfixTestId(props.testId, 'body-segment'),
          children: [metadataTable, props.body]
        }) : null, /*#__PURE__*/_jsx(TileFooter
        // customFooterRef is used to toggle the tabIndex for the custom content, so we only
        // need to set it when footerContent is present
        , {
          ref: props.footerContent ? customFooterRef : undefined,
          testId: postfixTestId(props.testId, 'footer'),
          objectMarkers: props.objectMarkers?.map((objectMarkerProps, i) => ({
            ...objectMarkerProps,
            focusable: objectMarkerProps.focusable !== false && containerTabIndex === -1,
            id: objectMarkerIds[i]
          })),
          children: props.footerContent
        })]
      }) : null]
    })]
  });
});
const DraggableTile = /*#__PURE__*/forwardRef(function DraggableTileContainer(props, ref) {
  const {
    dndProps,
    ...tileProps
  } = props;
  const tileRef = useRef(null);
  const {
    useDrag,
    getEmptyImage
  } = useReactDndConstructs();
  const dt = useDesignTokens();
  const dndStyle = getDndDesignTokens(dt);
  const earcon = useEarcon({
    source: EarconName.InputFull
  });
  const [{
    dragClassName
  }, drag, preview] = useDrag({
    type: TileDragType,
    item: props,
    canDrag: dndProps.allowDrag,
    collect: monitor => ({
      dragClassName: cx(monitor.isDragging() && cn.draggedItem)
    }),
    end: (_draggedItem, monitor) => {
      const dropResult = monitor.getDropResult();
      if (!dropResult) {
        earcon.play();
      }
    }
  });
  useEffect(() => {
    preview(getEmptyImage(), {
      captureDraggingState: true
    });
  }, [preview, getEmptyImage]);
  drag(tileRef);
  const dropMode = props.dndProps.dropMode ?? 'insert';
  const {
    dropClassName,
    tileDropZoneConnect
  } = useTileDroppable({
    ...props.dndProps,
    index: props.index
  });
  tileDropZoneConnect(tileRef);
  return /*#__PURE__*/_jsx("div", {
    className: cx(cn.dropContainer, dropMode === 'insert' && dropClassName),
    style: dndStyle,
    children: /*#__PURE__*/_jsx(NonDraggableTile, {
      ...tileProps,
      ref: e => {
        setRef(ref, e);
        setRef(tileRef, e);
      },
      style: tileProps.style,
      className: cx(tileProps.className, cn.draggable, dragClassName, dropMode === 'combine' && dropClassName)
    })
  });
});
export const Tile = /*#__PURE__*/forwardRef(function Tile({
  dndProps,
  ...props
}, ref) {
  return dndProps ? /*#__PURE__*/_jsx(DraggableTile, {
    ...props,
    dndProps: dndProps,
    ref: ref
  }) : /*#__PURE__*/_jsx(NonDraggableTile, {
    ...props,
    ref: ref
  });
});
const hasContent = props => !!(props.icon || props.avatarProps || props.title || props.description || props.menuButtonProps || props.showObjectMetadataTable || props.body || props.footerContent || props.objectMarkers && props.objectMarkers.length > 0);
//# sourceMappingURL=Tile.js.map